// /oauth/grant — MCP OAuth 2.1 consent page.
//
// docs/specs/mcp-read-only.md §13.4 step 5-8
//
// Claude.ai (or any other OAuth client that ran DCR against our
// /oauth/register) redirects the user's browser here with the full
// authorize query. We:
//   1. require a dashboard session (if no session → bounce /login?return=...)
//   2. fetch metadata from auth-api so we can show the actual client_name
//      + redirect_uri (so user can verify they're authorizing the right thing)
//   3. user clicks 「同意」 → POST /v1/oauth/authorize → 302 to client callback
//   4. user clicks 「拒絕」 → 302 to client callback with ?error=access_denied
//
// We deliberately load the consent page outside DashboardShell because
// Claude.ai opens the URL in a fresh browser tab — wrapping it in
// dashboard chrome would just confuse the user about what they're
// approving.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  KeyRound, ShieldCheck, AlertTriangle, ExternalLink, Loader2,
  Eye, PenLine, Zap, Globe2, Clock, Fingerprint, ArrowRight,
} from "lucide-react";
import {
  getAuthorizeMetadata, grantAuthorization,
  friendlyOAuthError,
  type AuthorizeMetadata, type AuthorizeQueryInput,
} from "../../dashboard/lib/oauthApi.js";
import { OAuthApiError } from "../../dashboard/lib/oauthApi.js";
import {
  getMcpToolsBreakdown,
  MCP_TOOLS_FALLBACK,
  type McpToolsByCategory,
} from "../../dashboard/lib/mcpHealthApi.js";

type Stage =
  | { status: "loading" }
  | { status: "needs_login" }
  | { status: "ready"; meta: AuthorizeMetadata }
  | { status: "error"; message: string };

export default function OAuthGrantPage(): React.ReactElement {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ status: "loading" });
  const [busy, setBusy] = useState<null | "grant" | "deny">(null);
  // Live tool-count breakdown from mcp.nexus.sentry.red/mcp/health.
  // Falls back to hardcoded constants when the cross-domain fetch fails
  // or returns a payload without the breakdown (older deploy).
  const [tools, setTools] = useState<McpToolsByCategory>(MCP_TOOLS_FALLBACK);

  // Snapshot the OAuth query params once on mount — we'll echo them back
  // to the server on POST so the user can't tamper between GET and POST.
  const params: AuthorizeQueryInput = useMemo(() => ({
    client_id: search.get("client_id") ?? "",
    redirect_uri: search.get("redirect_uri") ?? "",
    response_type: search.get("response_type") ?? "code",
    code_challenge: search.get("code_challenge") ?? "",
    code_challenge_method: search.get("code_challenge_method") ?? "",
    scope: search.get("scope") ?? "mcp:read",
    state: search.get("state") ?? "",
  // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot intentional
  }), []);

  // Fetch live tool breakdown in parallel with consent metadata so the
  // consent UI numbers stay correct as the whitelist grows.
  useEffect(() => {
    const ctrl = new AbortController();
    void getMcpToolsBreakdown(ctrl.signal).then((b) => setTools(b));
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const meta = await getAuthorizeMetadata(params);
        setStage({ status: "ready", meta });
      } catch (err) {
        if (err instanceof OAuthApiError && (err.status === 401 || err.code === "no_actor" || err.code === "no_user_id")) {
          setStage({ status: "needs_login" });
          return;
        }
        setStage({ status: "error", message: friendlyOAuthError(err) });
      }
    })();
  }, [params]);

  // Auto-bounce to /login with return URL when no session.
  useEffect(() => {
    if (stage.status !== "needs_login") return;
    const here = window.location.pathname + window.location.search;
    navigate(`/login?return_to=${encodeURIComponent(here)}`, { replace: true });
  }, [stage.status, navigate]);

  const onGrant = useCallback(async () => {
    if (stage.status !== "ready") return;
    setBusy("grant");
    try {
      const url = await grantAuthorization(params);
      window.location.replace(url);
    } catch (err) {
      setStage({ status: "error", message: friendlyOAuthError(err) });
      setBusy(null);
    }
  }, [stage.status, params]);

  const onDeny = useCallback(() => {
    if (stage.status !== "ready") return;
    setBusy("deny");
    // Per RFC 6749 §4.1.2.1, redirect to client with ?error=access_denied + state.
    const u = new URL(params.redirect_uri);
    u.searchParams.set("error", "access_denied");
    u.searchParams.set("error_description", "user denied the authorization request");
    if (params.state) u.searchParams.set("state", params.state);
    window.location.replace(u.toString());
  }, [stage.status, params]);

  return (
    <div className="oauth-grant-shell min-h-screen relative flex items-center justify-center p-4 sm:p-6 overflow-hidden">
      {/* Ambient backdrop — radial accent glow + subtle grid */}
      <BackdropFx />

      <div className="oauth-grant-card relative w-full max-w-lg">
        {/* Animated gradient border ring */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-accent/40 via-success/20 to-accent/40 opacity-60 blur-md pointer-events-none" aria-hidden />
        <div className="relative rounded-2xl border border-accent/30 bg-bg-surface/95 backdrop-blur-xl p-6 sm:p-7 shadow-[0_30px_80px_-20px_rgba(0,212,255,0.25)]">
          {/* Header */}
          <div className="flex items-start gap-3 mb-6">
            <span className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-accent/15 border border-accent/40 shadow-[inset_0_0_12px_rgba(0,212,255,0.25)]">
              <KeyRound className="h-5 w-5 text-accent relative z-10" />
              <span className="absolute inset-0 rounded-xl bg-accent/15 animate-pulse" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-semibold text-text-primary leading-tight">授權外部 LLM 存取</h1>
              <p className="mt-0.5 text-2xs font-mono uppercase tracking-[0.18em] text-accent/80">SENTRY · LOCATION · MCP</p>
            </div>
          </div>

          {stage.status === "loading" && (
            <div className="py-12 flex flex-col items-center gap-3 text-text-muted text-sm">
              <Loader2 className="h-5 w-5 animate-spin text-accent" />
              <span className="font-mono text-2xs uppercase tracking-widest">verifying request…</span>
            </div>
          )}

          {stage.status === "needs_login" && (
            <div className="py-8 flex flex-col items-center gap-3 text-text-muted text-sm">
              <Loader2 className="h-5 w-5 animate-spin text-accent" />
              <span className="font-mono text-2xs uppercase tracking-widest">redirecting to login…</span>
            </div>
          )}

          {stage.status === "error" && (
            <ErrorCard message={stage.message} onClose={() => navigate("/dashboard/settings", { replace: true })} />
          )}

          {stage.status === "ready" && (
            <ConsentCard
              meta={stage.meta}
              tools={tools}
              busy={busy}
              onGrant={() => void onGrant()}
              onDeny={onDeny}
            />
          )}
        </div>

        {/* Brand foot — tiny wordmark + audit-trail reassurance */}
        <p className="mt-3 text-center text-[10px] font-mono uppercase tracking-[0.22em] text-text-muted">
          SENTRY · 所有授權皆寫入 audit_log
        </p>
      </div>
    </div>
  );
}

function BackdropFx(): React.ReactElement {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <div className="absolute inset-0 bg-bg-base" />
      {/* Radial accent glow top-left */}
      <div className="absolute -top-1/2 -left-1/2 h-[120vh] w-[120vw] rounded-full bg-accent/10 blur-3xl" />
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      {/* Soft success glow bottom-right */}
      <div className="absolute -bottom-1/3 -right-1/4 h-[80vh] w-[80vw] rounded-full bg-success/10 blur-3xl" />
    </div>
  );
}

function ConsentCard({
  meta, tools, busy, onGrant, onDeny,
}: {
  meta: AuthorizeMetadata;
  tools: McpToolsByCategory;
  busy: null | "grant" | "deny";
  onGrant: () => void;
  onDeny: () => void;
}): React.ReactElement {
  let redirectHost = "";
  try { redirectHost = new URL(meta.redirect_uri).host; } catch { /* shown blank */ }

  const isReturning = !!meta.client.last_used_at;
  const country = meta.requester_country?.toUpperCase() ?? null;

  return (
    <>
      {/* Requesting client — primary identity block */}
      <div className="rounded-xl border border-accent/20 bg-gradient-to-br from-accent/[0.08] to-transparent p-4 mb-5">
        <div className="flex items-start gap-3">
          <ClientAvatar name={meta.client.client_name} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-text-primary truncate">{meta.client.client_name}</h2>
              <span className={
                "text-[10px] font-mono uppercase tracking-wider rounded px-1.5 py-0.5 border " +
                (isReturning
                  ? "border-success/40 bg-success/10 text-success"
                  : "border-warning/40 bg-warning/10 text-warning")
              }>
                {isReturning ? "已連線過" : "首次連線"}
              </span>
            </div>
            <p className="mt-0.5 text-2xs text-text-muted">
              想要存取您 SENTRY NEXUS 內的資料
            </p>
            <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
              {meta.client.software_id && (
                <>
                  <dt className="text-text-muted">Software</dt>
                  <dd className="font-mono text-text-secondary truncate">{meta.client.software_id}</dd>
                </>
              )}
              <dt className="text-text-muted">Client ID</dt>
              <dd className="font-mono text-text-secondary truncate" title={meta.client.client_id}>
                {meta.client.client_id.slice(0, 14)}…
              </dd>
              <dt className="text-text-muted">首次註冊</dt>
              <dd className="font-mono text-text-secondary">{shortDate(meta.client.created_at)}</dd>
              {meta.client.last_used_at && (
                <>
                  <dt className="text-text-muted">最後使用</dt>
                  <dd className="font-mono text-text-secondary">{shortDate(meta.client.last_used_at)}</dd>
                </>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Capabilities — honest disclosure of read + write */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <Fingerprint className="h-3.5 w-3.5 text-accent" />
          <h3 className="text-2xs font-mono uppercase tracking-widest text-text-secondary">授權後能做的事</h3>
          <span className="ml-auto text-2xs font-mono text-text-muted tabular-nums">
            {tools.total} tools
          </span>
        </div>

        <div className="space-y-2">
          <CapabilityRow
            icon={<Eye className="h-4 w-4" />}
            tone="ok"
            title="讀取資料"
            count={`${tools.read} tool`}
            detail="案場 / 客戶 / 地點 / 路線 / 任務 / 排班 / 報表 / 通知記錄 / 衛星圖層摘要"
          />
          <CapabilityRow
            icon={<PenLine className="h-4 w-4" />}
            tone="warn"
            title="寫入 & 修改"
            count={`${tools.write} tool`}
            detail="建立 / 修改客戶、案場、建築、樓層、地點、路線、任務、派發、班別、排班、自訂角色、權限指派"
          />
          <CapabilityRow
            icon={<Zap className="h-4 w-4" />}
            tone="warn"
            title="執行操作"
            count={`${tools.execute} tool`}
            detail="觸發衛星圖磚擷取、發起視覺資產升級 pipeline（消耗外部 API + R2 儲存）"
          />
        </div>

        <div className="mt-3 rounded-md border border-warning/30 bg-warning/[0.06] p-2.5 text-[11px] text-warning leading-relaxed flex items-start gap-1.5">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>
            <strong>立即生效、無 review modal。</strong>
            授權範圍 = 您在 dashboard 內的 RBAC 權限全集；不會超出您本人能做的、也不能跨租戶讀寫。
          </span>
        </div>
      </div>

      {/* Lifecycle / revocation reassurance */}
      <ul className="space-y-1.5 text-[11px] text-text-secondary mb-5">
        <li className="flex items-start gap-1.5">
          <Clock className="h-3 w-3 mt-0.5 text-text-muted flex-shrink-0" />
          <span>
            30 天內 <strong>{meta.client.client_name}</strong> 可代表您持續存取（access token 1h + refresh 自動續）
          </span>
        </li>
        <li className="flex items-start gap-1.5">
          <ShieldCheck className="h-3 w-3 mt-0.5 text-text-muted flex-shrink-0" />
          <span>
            可隨時於 <code className="font-mono text-[10px] bg-bg-base px-1 rounded border border-border">/dashboard/settings</code> →「已連線的外部 LLM 應用」撤銷
          </span>
        </li>
      </ul>

      {/* Tech meta footer — redirect host + user country */}
      <div className="rounded-lg border border-border bg-bg-raised/60 p-3 mb-5 space-y-2">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-text-muted">
            <ExternalLink className="h-3 w-3" />
            <span>授權成功後將跳轉至</span>
          </div>
          <code className="mt-1 block font-mono text-xs break-all text-accent">
            {redirectHost || meta.redirect_uri}
          </code>
        </div>
        {country && (
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-text-muted">
              <Globe2 className="h-3 w-3" />
              <span>您的請求來自</span>
            </div>
            <code className="font-mono text-xs text-text-secondary">
              {flagEmoji(country)} {country}
            </code>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={onDeny}
          disabled={busy !== null}
          className="flex-1 rounded-lg border border-border bg-bg-raised px-4 py-2.5 text-sm text-text-secondary hover:bg-bg-base hover:text-text-primary transition-colors disabled:opacity-50"
        >
          {busy === "deny" ? "處理中…" : "拒絕"}
        </button>
        <button
          type="button"
          onClick={onGrant}
          disabled={busy !== null}
          className="flex-1 group relative rounded-lg bg-gradient-to-r from-accent to-accent/80 px-4 py-2.5 text-sm font-semibold text-bg-base shadow-[0_8px_24px_-8px_rgba(0,212,255,0.6)] hover:shadow-[0_8px_28px_-6px_rgba(0,212,255,0.85)] hover:brightness-110 transition-all disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        >
          {busy === "grant" ? "處理中…" : (<>同意授權 <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></>)}
        </button>
      </div>
    </>
  );
}

function ClientAvatar({ name }: { name: string }): React.ReactElement {
  // First letter as monogram; gradient backdrop in accent-success palette.
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  return (
    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-accent/30 to-success/20 border border-accent/40 font-semibold text-accent text-base">
      {initial}
    </span>
  );
}

function CapabilityRow({
  icon, tone, title, count, detail,
}: {
  icon: React.ReactNode;
  tone: "ok" | "warn";
  title: string;
  count: string;
  detail: string;
}): React.ReactElement {
  const toneCls = tone === "ok"
    ? "border-success/25 bg-success/[0.04]"
    : "border-warning/25 bg-warning/[0.05]";
  const iconCls = tone === "ok" ? "text-success" : "text-warning";
  return (
    <div className={"rounded-md border p-2.5 " + toneCls}>
      <div className="flex items-start gap-2">
        <span className={"flex-shrink-0 " + iconCls}>{icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-text-primary">{title}</span>
            <span className="text-[10px] font-mono tabular-nums text-text-muted">{count}</span>
          </div>
          <p className="mt-0.5 text-[11px] text-text-secondary leading-snug">{detail}</p>
        </div>
      </div>
    </div>
  );
}

function shortDate(iso: string): string {
  try {
    const d = new Date(iso);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mm = String(d.getUTCMinutes()).padStart(2, "0");
    return `${y}-${m}-${day} ${hh}:${mm}Z`;
  } catch { return iso; }
}

function flagEmoji(cc: string): string {
  if (!/^[A-Z]{2}$/.test(cc)) return "🌐";
  return String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0)));
}

function ErrorCard({ message, onClose }: { message: string; onClose: () => void }): React.ReactElement {
  return (
    <>
      <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger flex items-start gap-2 mb-4">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>{message}</span>
      </div>
      <button type="button" onClick={onClose} className="btn-secondary text-sm w-full">
        關閉
      </button>
    </>
  );
}
